import type { Preferences, ProgressState, RoundResult } from '../domain/types'
import { DEFAULT_PROGRESSION_IDS, DEFAULT_SCALE_IDS } from '../domain/curriculum'

const STORAGE_KEY = 'tonaltutor-progress-v1'

export const DEFAULT_PREFERENCES: Preferences = {
  difficulty: 'beginner',
  instrument: 'studio-piano',
  roundLength: 10,
  octaveRange: 1,
  playbackStyle: 'harmonic',
  playbackSpeed: 1,
  answerInput: 'labels',
  showPitchRail: true,
  absolutePitchSource: 'random',
  absolutePitchTonic: 0,
  absolutePitchScaleId: 'ionian',
  selectedScaleIds: DEFAULT_SCALE_IDS,
  selectedProgressionIds: DEFAULT_PROGRESSION_IDS,
  theme: 'studio',
  textSize: 'standard',
}

export const EMPTY_PROGRESS: ProgressState = {
  version: 1,
  preferences: DEFAULT_PREFERENCES,
  rounds: [],
  currentStreak: 0,
  longestStreak: 0,
  lastPracticeDate: null,
}

function isSameDay(first: Date, second: Date): boolean {
  return first.getFullYear() === second.getFullYear()
    && first.getMonth() === second.getMonth()
    && first.getDate() === second.getDate()
}

function isYesterday(date: Date, comparison: Date): boolean {
  const yesterday = new Date(comparison)
  yesterday.setDate(yesterday.getDate() - 1)
  return isSameDay(date, yesterday)
}

export function loadProgress(): ProgressState {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (!stored) return EMPTY_PROGRESS
    const parsed = JSON.parse(stored) as Partial<ProgressState>
    if (parsed.version !== 1) return EMPTY_PROGRESS
    return {
      ...EMPTY_PROGRESS,
      ...parsed,
      preferences: { ...DEFAULT_PREFERENCES, ...parsed.preferences },
      rounds: Array.isArray(parsed.rounds) ? parsed.rounds.slice(0, 250) : [],
    }
  } catch {
    return EMPTY_PROGRESS
  }
}

export function saveProgress(progress: ProgressState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
  } catch {
    // Private browsing and full storage can fail; the session remains usable.
  }
}

export function addRound(progress: ProgressState, result: RoundResult): ProgressState {
  const today = new Date(result.completedAt)
  const previous = progress.lastPracticeDate ? new Date(progress.lastPracticeDate) : null
  let currentStreak = progress.currentStreak

  if (!previous || (!isSameDay(previous, today) && !isYesterday(previous, today))) currentStreak = 1
  else if (isYesterday(previous, today)) currentStreak += 1

  return {
    ...progress,
    rounds: [result, ...progress.rounds].slice(0, 250),
    currentStreak,
    longestStreak: Math.max(progress.longestStreak, currentStreak),
    lastPracticeDate: result.completedAt,
  }
}

export function resetProgress(): ProgressState {
  window.localStorage.removeItem(STORAGE_KEY)
  return EMPTY_PROGRESS
}
